import { motion, AnimatePresence } from "framer-motion";
import { LanguageSwitcher } from "@/components/LanguageSwitcher.js";
import { useBgmStore } from "@/store/bgm.js";
import { uiStrings } from "@bongeunsa/i18n";
import type { Language } from "@bongeunsa/i18n";

interface PublicNavProps {
  lang: Language;
  onHomeClick?: (() => void) | null;
  title?: string;
}

export function PublicNav({ lang, onHomeClick, title }: PublicNavProps) {
  const { playing, toggle } = useBgmStore();
  const s = uiStrings[lang];

  const homeContent = (
    <span className="flex items-center gap-1.5 shrink-0">
      <img
        src="/logo-symbol.png"
        alt="봉은사 심볼"
        className="h-5 w-5 object-contain"
        draggable={false}
      />
      <span className="text-sm font-bold tracking-wide text-gray-800">{s.site.name}</span>
    </span>
  );

  return (
    <header className="sticky top-3 z-40 mx-3 flex items-center justify-between gap-2 rounded-[22px] glass-nav px-4 py-2.5">
      {/* 왼쪽: 로고 [› 제목] */}
      <div className="flex items-center gap-1.5 min-w-0 flex-1 overflow-hidden">
        {onHomeClick ? (
          <button type="button" onClick={onHomeClick} className="hover:opacity-75 transition-opacity shrink-0">
            {homeContent}
          </button>
        ) : (
          <a href="/" className="hover:opacity-75 transition-opacity shrink-0">
            {homeContent}
          </a>
        )}

        {title && (
          <>
            {/* 구분자 chevron-right */}
            <svg className="h-3 w-3 shrink-0 text-gray-300" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            <span className="truncate text-xs font-medium text-gray-500 min-w-0">{title}</span>
          </>
        )}
      </div>

      {/* 오른쪽: BGM + 언어 */}
      <div className="flex items-center gap-1 shrink-0">
        <AnimatePresence>
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            type="button"
            title={s.nav.bgmLabel}
            onClick={toggle}
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-black/5 hover:text-brand-600 transition-colors"
          >
            {playing ? (
              <span className="flex items-end gap-0.5 h-4">
                {[0, 1, 2].map((i) => (
                  <motion.span
                    key={i}
                    className="block w-0.5 rounded-full bg-brand-500"
                    animate={{ scaleY: [0.4, 1, 0.4, 0.8, 0.4] }}
                    transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.18, ease: "easeInOut" }}
                    style={{ height: "10px", transformOrigin: "bottom" }}
                  />
                ))}
              </span>
            ) : (
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6z" />
              </svg>
            )}
          </motion.button>
        </AnimatePresence>

        <LanguageSwitcher />
      </div>
    </header>
  );
}
